import Column from '@components/Column';
import Dropdown from '@components/Dropdown';
import ImageUploader from '@components/ImageUploader';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { useFlow } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import useInquiryForm from './hooks/useInquiryForm';
import useInquiryTypeContent from './hooks/useInquiryTypeContent';
import useInquiryTypes from './hooks/useInquiryTypes';

interface Props {
    values: ReturnType<typeof useInquiryForm>;
}

function InquiryFormContents({ values }:Props) {
    const {
        content, setContent, title, setTitle, inquiryTypeId, fileUrls, setFileUrls,
        typeError, titleError, contentError,
    } = values;
    const { t } = useTranslation('inquiry');
    const { push } = useFlow();

    const inquiryTypes = useInquiryTypes();
    useInquiryTypeContent({ inquiryTypeId, setContent, inquiryTypes });

    return (
        <Container fullWidth spacing={6} >
            <Column spacing={2} fullWidth>
                <Text fontWeight='medium' color={colors.textPrimary}>{t('form.type')}</Text>
                <Dropdown
                    value={inquiryTypeId}
                    onClick={() => push('InquiryTypeBottomSheet', {})}
                    placeholder={t('form.typePlaceholder')}
                    errorText={typeError}
                    options={inquiryTypes?.map((inquiryType) => ({
                        value: inquiryType.inquiryTypeId, label: inquiryType.name,
                    }))} />
            </Column>
            <Column spacing={1} fullWidth>
                <FieldLabel>{t('form.title')}</FieldLabel>
                <TextField placeholder={t('form.titlePlaceholder')}
                    maxLength={100}
                    value={title}
                    errorText={titleError}
                    onChange={(e) => setTitle(e.target.value)} />
            </Column>
            <Column spacing={1} fullWidth>
                <FieldLabel>{t('form.content')}</FieldLabel>
                <TextField placeholder={t('form.contentPlaceholder')}
                    value={content}
                    errorText={contentError}
                    onChange={(e) => setContent(e.target.value)}
                    as='textarea'
                    maxLength={200}
                    showLength
                    rows={4}
                />
            </Column>
            <Column spacing={1} fullWidth>
                <FieldLabel>{t('form.image')}</FieldLabel>
                <ImageUploader caption={t('form.imageCaption')}
                    value={fileUrls}
                    setValue={setFileUrls}
                    maxLength={2}
                    placeholder={t('form.imagePlaceholder')} />
            </Column>
        </Container>
    );
}

function FieldLabel({ children }: { children: string }) {
    return (
        <Text fontWeight='medium' color={colors.textSecondary}>{children}</Text>
    );
}

const Container = styled(Column)`
    padding: var(--spacing-4);
    min-height: calc(100vh - 205px);
`;

export default InquiryFormContents;
